'use client';

import { Message } from '@depop/web-thrift-ui/Icons/Message';
import { MyDepopOutline } from '@depop/web-thrift-ui/Icons/MyDepopOutline';
import { Caption } from '@depop/web-thrift-ui/Typography';
import { Modal } from '@depop/web-ui-kit/Modal';
import { useTranslations } from 'next-intl';

import styles from './CondensedProductCard.module.css';

import { ShopSummaryActionsCta } from '@/app/[locale]/products/[slug]/components/ShopSummaryActions/ShopSummaryActionsCta';
import { useSellerActions } from '@/app/[locale]/products/[slug]/hooks/useSellerActions';
import { ButtonExperiment } from '@/components/ButtonExperiment';
import { DownloadAppModal } from '@/components/DownloadAppModal/DownloadAppModal';
import { ExtendedLink } from '@/components/ExtendedLink';
import { SellerIdentityRow } from '@/components/SellerIdentityRow';
import { TrustSignals } from '@/components/TrustSignals';
import { useProductBySlugExtendedPDP } from '@/modules/product/hooks/useProductBySlugExtendedPDP';

export function CondensedSellerRow() {
  const t = useTranslations('product');
  const { data: productExtended } = useProductBySlugExtendedPDP();
  const {
    isAuthenticated,
    seller,
    shopHref,
    messagesUrl,
    handleVisitShopClick,
    handleMessageClick,
    isDownloadAppModalOpen,
    setIsDownloadAppModalOpen,
  } = useSellerActions();

  if (!seller) {
    return null;
  }

  const avatarSrc = seller.picture?.[150] || '';

  return (
    <div className={styles.sellerRow}>
      <SellerIdentityRow
        seller={{
          id: seller.id,
          username: seller.username,
          initials: seller.initials,
          verified: seller.verified,
          avatarUrl: avatarSrc,
        }}
        shopHref={shopHref}
        avatarAlt={t('ProfileLinkAria', { username: seller.username })}
        feedbackRating={productExtended?.seller_reviews?.reviews_rating}
        feedbackCount={productExtended?.seller_reviews?.reviews_total}
      >
        {productExtended && (
          <TrustSignals
            activeDate={productExtended.seller_activity?.last_seen}
            sold={productExtended.seller_activity?.items_sold}
            className={styles.condensedTrustSignals}
          />
        )}
      </SellerIdentityRow>

      <div className={styles.sellerActionsMobile}>
        <ExtendedLink
          href="/[username]"
          as={shopHref}
          onClick={handleVisitShopClick}
          aria-label={t('SellerCta.Shop')}
        >
          <span className={styles.iconButton}>
            <MyDepopOutline size="sm" />
          </span>
        </ExtendedLink>

        {isAuthenticated ? (
          <ExtendedLink
            href={messagesUrl}
            onClick={handleMessageClick}
            aria-label={t('MessageSeller')}
          >
            <span className={styles.iconButton}>
              <Message size="sm" />
            </span>
          </ExtendedLink>
        ) : (
          <button
            className={styles.iconButton}
            onClick={handleMessageClick}
            aria-label={t('MessageSeller')}
            type="button"
          >
            <Message size="sm" />
          </button>
        )}
      </div>

      <div className={styles.sellerActionsDesktop}>
        <ShopSummaryActionsCta
          outline
          className={styles.sellerActionButton}
          href={shopHref}
          onClick={handleVisitShopClick}
        >
          <Caption as="span" colour="base" variant="sm-regular">
            {t('SellerCta.Shop')}
          </Caption>
        </ShopSummaryActionsCta>
        {isAuthenticated ? (
          <ShopSummaryActionsCta
            outline
            className={styles.sellerActionButton}
            href={messagesUrl}
            onClick={handleMessageClick}
          >
            <Caption as="span" colour="base" variant="sm-regular">
              {t('SellerCta.Question')}
            </Caption>
          </ShopSummaryActionsCta>
        ) : (
          <ButtonExperiment
            variant="secondary"
            legacyClassName={styles.sellerActionButton}
            onClick={handleMessageClick}
          >
            <Caption as="span" colour="base" variant="sm-regular">
              {t('SellerCta.Question')}
            </Caption>
          </ButtonExperiment>
        )}
      </div>

      {!isAuthenticated && (
        <Modal
          isOpen={isDownloadAppModalOpen}
          setOpen={setIsDownloadAppModalOpen}
          content={() => (
            <DownloadAppModal downloadAppTrackingId="pdp_condensed_seller_row" />
          )}
        />
      )}
    </div>
  );
}
